import React, { useEffect, useState } from "react";
import Box from "@material-ui/core/Box";
import { Icon, SecondaryButton } from "@prestosports/commons-ui";
import Reset32 from "@carbon/icons-react/lib/reset/32";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useSeasonContext } from "../../../state/season/context";
import commonStyles from "../../common/styles/Common.styles";
import Configuration from "../../common/components/Configuration";
import RestoreSettings from "../../common/components/RestoreSettings";
import RuleSetSelect from "../../common/components/RuleSetSelect";
import seasonSettingsStyles from "../styles/SeasonSettings.styles";
import { useSportContext } from "../../../state/sport/context";
import { useAppContext } from "../../../state/app/context";
import SettingConfirmationDialog from "../../common/components/SettingConfirmationDialog";
import RuleSetConfirmationDialog from "../../common/components/RuleSetConfirmationDialog";
import { getSportConfiguration } from "../../common/config/sport-configurations";

const SeasonSettings = () => {
	const commonClasses = commonStyles({ withHeader: false });
	const classes = seasonSettingsStyles();
	const xsScreen = useMediaQuery((theme) => theme.breakpoints.down("xs"));

	const {
		state: appState,
		setConfirmSettingSave,
		resetConfirmSettingSave
	} = useAppContext();
	const confirmSaveOpen = appState.confirmSettingSave();
	const settingChangeSummary = appState.settingChangeSummary();

	const {
		state: sportState,
		getRuleSetOptions,
		getCustomValidation
	} = useSportContext();
	const ruleSetOptions = sportState.ruleSetOptions();
	const customValidation = getCustomValidation();

	const {
		state,
		saveDefaultGameConfiguration,
		restoreDefaultGameConfiguration,
		setRuleSetId
	} = useSeasonContext();
	const defaultGameConfiguration = state.defaultGameConfiguration();
	const customFormData = state.customFormData();
	const currentSeason = state.currentSeason();

	const { ruleSetId, sport } = currentSeason || {};

	const [restoreConfirmationOpen, setRestoreConfirmationOpen] = useState(false);
	const [confirmRulesOpen, setConfirmRulesOpen] = useState(false);
	const [pendingRuleSet, setPendingRuleSet] = useState(null);
	const [pendingConfig, setPendingConfig] = useState(null);

	const onRestoreDefaults = () => {
		setRestoreConfirmationOpen(true);
	};

	const confirmLoadRules = () => {
		setRuleSetId(pendingRuleSet);
		setConfirmRulesOpen(false);
	};

	const cancelLoadRules = () => {
		setConfirmRulesOpen(false);
	};

	const confirmRestore = () => {
		restoreDefaultGameConfiguration();
		setRestoreConfirmationOpen(false);
	};

	const cancelRestore = () => {
		setRestoreConfirmationOpen(false);
	};

	const handleConfirmSave = (updatedConfig) => {
		setPendingConfig(updatedConfig);
		setConfirmSettingSave(updatedConfig);
	};

	const handleSave = () => {
		saveDefaultGameConfiguration(pendingConfig);
		resetConfirmSettingSave();
	};

	useEffect(() => {
		getRuleSetOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<Box className={commonClasses.container}>
				<Box className={commonClasses.contentContainer}>
					<Configuration
						configuration={defaultGameConfiguration}
						customFormData={customFormData}
						customValidation={customValidation}
						onSaveConfig={handleConfirmSave}
						extension={getSportConfiguration(sport?.sportId)}
						secondaryActions={
							<Box className={classes.secondaryActionContainer}>
								<SecondaryButton
									label={xsScreen ? "Defaults" : "Restore defaults"}
									startIcon={
										<Icon>
											<Reset32 />
										</Icon>
									}
									onClick={onRestoreDefaults}
								/>
								<RuleSetSelect
									ruleSetOptions={ruleSetOptions}
									ruleSetId={ruleSetId}
									onRuleSelect={(e) => {
										setPendingRuleSet(e.target.value);
										setConfirmRulesOpen(true);
									}}
								/>
							</Box>
						}
					/>
				</Box>
			</Box>

			<SettingConfirmationDialog
				isOpen={confirmSaveOpen}
				onClose={resetConfirmSettingSave}
				onSave={handleSave}
				changeSummary={settingChangeSummary}
			/>

			<RuleSetConfirmationDialog
				restoreConfirmationOpen={confirmRulesOpen}
				confirmRestore={confirmLoadRules}
				cancelRestore={cancelLoadRules}
				ruleSetName={ruleSetOptions.find((o) => o.id === pendingRuleSet)?.name}
			/>

			<RestoreSettings
				restoreConfirmationOpen={restoreConfirmationOpen}
				confirmRestore={confirmRestore}
				cancelRestore={cancelRestore}
			/>
		</>
	);
};

export default SeasonSettings;
